/**
 * The one fetch helper. Every call to the BFF goes through an ApiClient:
 *
 *  - same-origin only (paths must start with /api/ or /auth/), cookies included;
 *    the request goes to `<base path><path>` ('/admin/api/...' behind /admin, lib/basePath.ts)
 *  - JSON in, JSON out
 *  - non-GET requests carry the session's CSRF token in `x-billing-csrf`
 *  - a 401 calls `onUnauthorized` (AuthContext sends the user to /login)
 *  - errors throw ApiError with the BFF's `{error, detail}`
 *
 * Get one with `useApi()` (auth/AuthContext.tsx). Area hooks in
 * web/src/api/<area>.ts wrap it with react-query.
 */

import { readBasePath } from '@/lib/basePath';

export const CSRF_HEADER = 'x-billing-csrf';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The full JSON error body (e.g. `issues` on a 422), when there was one. */
    readonly body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  getCsrfToken: () => string | null;
  onUnauthorized: () => void;
  fetchImpl?: typeof fetch;
  /** Mount point prepended to every path ('' or e.g. '/admin'). Default: read from the page's <base>. */
  basePath?: string;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiClient {
  request<T>(method: Method, path: string, body?: unknown): Promise<T>;
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
}

export async function readError(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as { error?: unknown; detail?: unknown; [k: string]: unknown } | null;
  const code = typeof body?.error === 'string' ? body.error : `http_${res.status}`;
  const detail = typeof body?.detail === 'string' ? body.detail : `Request failed (HTTP ${res.status})`;
  return new ApiError(res.status, code, detail, body && typeof body === 'object' ? body : null);
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const doFetch = opts.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const base = opts.basePath ?? readBasePath();

  async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
    if (!/^\/(api|auth)\//.test(path)) throw new Error(`api: refusing non-BFF path ${path}`);
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') {
      const token = opts.getCsrfToken();
      if (token) headers[CSRF_HEADER] = token;
    }
    const res = await doFetch(`${base}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    if (res.status === 401) {
      opts.onUnauthorized();
      throw await readError(res);
    }
    if (!res.ok) throw await readError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    request,
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
    patch: (path, body) => request('PATCH', path, body),
    delete: (path) => request('DELETE', path),
  };
}
